import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronLeft, ChevronRight, ChevronDown, X, AlertCircle, Search, Plus, Trash2, Calendar as CalendarIcon } from 'lucide-react';
import { format as dateFnsFormat } from 'date-fns';
import { useDropdownPosition } from '../../hooks/useDropdownPosition';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Calendar } from './calendar';

/* ─── Yes / No Chip Toggle ─────────────────────────────────────────────────── */
export const YesNoChips: React.FC<{
  label?: string;
  required?: boolean;
  value: string | boolean | undefined;
  onChange: (v: any) => void;
  disabled?: boolean;
  error?: string;
}> = ({ label, required, value, onChange, disabled, error }) => {
  const strValue = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : (value || '');

  return (
    <div>
      {label && (
        <p className="mb-2 text-[11.5px] font-medium text-slate-500 dark:text-slate-400 select-none">
          {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </p>
      )}

      {/* Segmented control — bordered track */}
      <div
        className={[
          'inline-flex items-center rounded-lg overflow-hidden',
          'border border-slate-200 dark:border-white/[0.12]',
          'bg-slate-50 dark:bg-white/[0.04]',
          disabled ? 'opacity-50 pointer-events-none' : '',
        ].join(' ')}
      >
        {(['Yes', 'No'] as const).map((opt, idx) => {
          const isSelected = strValue === opt;
          return (
            <button
              key={opt}
              type="button"
              disabled={disabled}
              onClick={() => !disabled && onChange(typeof value === 'boolean' ? opt === 'Yes' : opt)}
              className={[
                'relative inline-flex items-center gap-1.5 px-4 py-[6px]',
                'text-[12.5px] font-semibold select-none',
                'transition-all duration-150 ease-out',
                idx === 0 ? '' : 'border-l border-slate-200 dark:border-white/[0.12]',
                isSelected
                  ? opt === 'Yes'
                    ? 'bg-brand-600 text-white'
                    : 'bg-slate-700 dark:bg-slate-600 text-white'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-white dark:hover:bg-white/[0.04]',
                !disabled && 'cursor-pointer',
              ].join(' ')}
            >
              {isSelected && (
                <Check size={11} strokeWidth={3} className="shrink-0" />
              )}
              {opt}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="flex items-center gap-1 mt-1.5">
          <AlertCircle size={11} className="text-red-500 shrink-0" />
          <p className="text-[11px] text-red-500">{error}</p>
        </div>
      )}
    </div>
  );
};


/* ─── Checkbox ──────────────────────────────────────────────────────── */
export const ModernCheckbox: React.FC<{
  checked: boolean;
  onChange: (c: boolean) => void;
  label: string;
  disabled?: boolean;
}> = ({ checked, onChange, label, disabled }) => (
  <button type="button" disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className={[
      'inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border-[1.5px] select-none',
      'transition-all duration-200 text-[12.5px]',
      checked
        ? 'border-brand-500 bg-brand-50/70 dark:bg-brand-500/10 shadow-sm'
        : 'border-slate-200 dark:border-white/[0.10] hover:border-brand-300 hover:bg-brand-50/20 dark:hover:bg-brand-500/[0.04]',
      disabled ? 'cursor-default opacity-60' : 'cursor-pointer active:scale-[0.98]',
    ].join(' ')}
  >
    <div className={[
      'w-4 h-4 rounded-[5px] border-[1.5px] flex-shrink-0 flex items-center justify-center',
      'transition-all duration-200',
      checked ? 'border-brand-500 bg-brand-500' : 'border-slate-300 dark:border-slate-600 bg-transparent',
    ].join(' ')}>
      {checked && <Check size={10} strokeWidth={3} className="text-white" />}
    </div>
    <span className={[
      'leading-none transition-colors',
      checked ? 'font-semibold text-brand-600 dark:text-brand-400' : 'text-slate-600 dark:text-slate-200',
    ].join(' ')}>{label}</span>
  </button>
);

/* ─── Pill Toggle ──────────────────────────────────────────────────────────── */
export const PillToggle: React.FC<{
  options: readonly string[];
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}> = ({ options, value, onChange, disabled }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((opt) => (
      <button
        key={opt}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && onChange(opt)}
        className={[
          'px-3.5 py-[6px] rounded-full text-[12px] font-semibold border transition-all duration-200',
          value === opt
            ? 'bg-brand-600 text-white border-brand-600 shadow-sm shadow-brand-600/20'
            : 'bg-white dark:bg-surface-dark-300 text-slate-500 dark:text-slate-300 border-slate-200 dark:border-white/[0.10] hover:border-brand-400 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-brand-50/30 dark:hover:bg-brand-500/[0.06]',
          disabled ? 'cursor-default opacity-60' : 'cursor-pointer active:scale-[0.97]',
        ].join(' ')}
      >
        {opt}
      </button>
    ))}
  </div>
);

export const NumField: React.FC<{
  label: string;
  unit: string;
  value: string;
  onChange: (v: string) => void;
  step?: number;
}> = ({ label, unit, value, onChange, step = 1 }) => (
  <div className="group/field">
    <div className="relative rounded-lg border border-slate-200 dark:border-white/[0.10] bg-white dark:bg-surface-dark-300 transition-all duration-200 focus-within:border-brand-400 dark:focus-within:border-brand-400/60 focus-within:ring-2 focus-within:ring-brand-500/[0.08] hover:border-slate-300 dark:hover:border-white/[0.16] h-[42px]">
      <input
        type="number"
        min={0}
        step={step}
        value={value}
        onChange={(e) => { const v = e.target.value; if (v === '' || Number(v) >= 0) onChange(v); }}
        placeholder=" "
        className="peer w-full h-full bg-transparent dark:text-slate-100 outline-none text-[13.5px] pl-3 pr-12 pt-4 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <label className={`absolute left-3 right-12 truncate pointer-events-none transition-all duration-200 text-slate-400 dark:text-slate-500
        ${value ? 'top-1.5 text-[10px] font-semibold uppercase tracking-wider text-brand-500 dark:text-brand-400' : 'top-1/2 -translate-y-1/2 text-[13px]'}
        peer-focus:top-1.5 peer-focus:translate-y-0 peer-focus:text-[10px] peer-focus:font-semibold peer-focus:uppercase peer-focus:tracking-wider peer-focus:text-brand-500 dark:peer-focus:text-brand-400`}>
        {label}
      </label>
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold uppercase tracking-wider text-slate-400 pointer-events-none">{unit}</span>
    </div>
  </div>
);

/* ─── CompactSelect ────────────────────────────────────────────────────────── */
export const CompactSelect: React.FC<{
  label: string;
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
  error?: string;
}> = ({ label, value, options, onChange, error }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const { triggerRef, position } = useDropdownPosition(open);
  const listRef = useRef<HTMLDivElement>(null);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasValue = !!value;

  const baseRing = error
    ? 'border-red-300 dark:border-red-500/50 focus-within:border-red-400 focus-within:ring-2 focus-within:ring-red-500/[0.08]'
    : 'border-slate-200 dark:border-white/[0.10] focus-within:border-brand-400 dark:focus-within:border-brand-400/60 focus-within:ring-2 focus-within:ring-brand-500/[0.08] hover:border-slate-300 dark:hover:border-white/[0.16]';

  const filtered = search.trim()
    ? options.filter(o => o.toLowerCase().includes(search.toLowerCase()))
    : [...options];

  useEffect(() => { setHighlightIdx(-1); }, [filtered.length, search]);
  useEffect(() => {
    if (highlightIdx >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-option]');
      items[highlightIdx]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx]);

  const handleClose = () => { setOpen(false); setSearch(''); setHighlightIdx(-1); };

  return (
    <div className="group/field relative" ref={triggerRef}>
      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={handleClose} />
          {position.ready && (
            <div
              ref={listRef}
              style={{
                position: 'fixed',
                zIndex: 9999,
                maxHeight: position.maxHeight,
                width: position.width,
                left: position.left,
                ...(position.direction === 'above' ? { bottom: position.bottom } : { top: position.top }),
              }}
              className="overflow-y-auto bg-white dark:bg-surface-dark-400 border border-slate-200/80 dark:border-white/[0.10] rounded-xl shadow-lg dark:shadow-dark-lg overscroll-contain animate-fade-in py-1"
            >
              {/* Search header when filtering */}
              {search.trim() && (
                <div className="px-3 pt-2 pb-1.5 flex items-center gap-1.5 border-b border-slate-100 dark:border-white/[0.06]">
                  <Search size={12} className="text-slate-400 flex-shrink-0" />
                  <span className="text-[11px] text-slate-400 truncate">
                    "<span className="font-semibold text-slate-600 dark:text-slate-300">{search}</span>"
                  </span>
                </div>
              )}
              {filtered.length > 0 ? (
                filtered.map((opt, idx) => (
                  <button key={opt} type="button" data-option
                    onMouseDown={(e) => { e.preventDefault(); onChange(opt); handleClose(); }}
                    onMouseEnter={() => setHighlightIdx(idx)}
                    className={[
                      'w-full text-left px-3 py-2 text-[13px] transition-colors',
                      idx === highlightIdx
                        ? 'bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400 font-semibold'
                        : opt === value
                          ? 'bg-brand-50/60 text-brand-600 dark:bg-brand-500/[0.06] dark:text-brand-400 font-medium'
                          : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/[0.04]',
                    ].join(' ')}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{opt}</span>
                      {opt === value && <Check size={12} className="text-brand-500 flex-shrink-0" />}
                    </div>
                  </button>
                ))
              ) : (
                <div className="px-3 py-4 text-[12px] text-slate-400 text-center">No match for "{search}"</div>
              )}
            </div>
          )}
        </>,
        document.body
      )}

      {/* Trigger field — matches FloatingLabelInput height & style */}
      <div
        className={`relative rounded-lg border bg-white dark:bg-surface-dark-300 transition-all duration-200 ${baseRing} h-[42px] cursor-pointer`}
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}
      >
        <input
          ref={inputRef}
          value={open ? search : value}
          readOnly={!open}
          placeholder=" "
          onChange={(e) => { setSearch(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIdx(i => Math.min(i + 1, filtered.length - 1)); if (!open) setOpen(true); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightIdx(i => Math.max(i - 1, 0)); }
            else if (e.key === 'Enter') {
              e.preventDefault();
              if (highlightIdx >= 0 && highlightIdx < filtered.length) { onChange(filtered[highlightIdx]); handleClose(); }
              else if (search.trim()) { const exact = options.find(o => o.toLowerCase() === search.trim().toLowerCase()); if (exact) { onChange(exact); handleClose(); } }
            }
            else if (e.key === 'Escape') handleClose();
          }}
          className="peer w-full h-full bg-transparent dark:text-slate-100 outline-none text-[13.5px] pl-3 pr-8 pt-4 cursor-pointer text-ellipsis"
        />
        <label className={[
          'absolute left-3 right-8 truncate pointer-events-none transition-all duration-200',
          hasValue || open
            ? 'top-1.5 text-[10px] font-semibold uppercase tracking-wider text-brand-500 dark:text-brand-400'
            : 'top-1/2 -translate-y-1/2 text-[13px] text-slate-400 dark:text-slate-500',
          'peer-focus:top-1.5 peer-focus:translate-y-0 peer-focus:text-[10px] peer-focus:font-semibold peer-focus:uppercase peer-focus:tracking-wider peer-focus:text-brand-500 dark:peer-focus:text-brand-400',
        ].join(' ')}>
          {label}
        </label>
        <ChevronDown
          size={13}
          className={`absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </div>

      {error && (
        <div className="flex items-center gap-1 mt-1 ml-0.5">
          <AlertCircle size={11} className="text-red-500 shrink-0" />
          <p className="text-[11px] text-red-500">{error}</p>
        </div>
      )}
    </div>
  );
};

/* ─── Floating Textarea ────────────────────────────────────────────────────── */
export const FloatingTextarea: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  required?: boolean;
  error?: string;
  readOnly?: boolean;
  icon?: React.ReactNode;
}> = ({ label, value, onChange, rows = 3, required, error, readOnly, icon }) => {
  const hasValue = !!value;
  const baseRing = error
    ? 'border-red-300 dark:border-red-500/50 focus-within:border-red-400 focus-within:ring-2 focus-within:ring-red-500/[0.08]'
    : 'border-slate-200 dark:border-white/[0.10] focus-within:border-brand-400 dark:focus-within:border-brand-400/60 focus-within:ring-2 focus-within:ring-brand-500/[0.08] hover:border-slate-300 dark:hover:border-white/[0.16]';

  return (
    <div className="group/field">
      <div className={`relative rounded-lg border bg-white dark:bg-surface-dark-300 transition-all duration-200 ${baseRing}`}>
        {icon && (
          <div className="absolute left-3 top-4 text-slate-400 dark:text-slate-500 group-focus-within/field:text-brand-500 transition-colors pointer-events-none [&>svg]:h-[14px] [&>svg]:w-[14px]">
            {icon}
          </div>
        )}
        <textarea
          rows={rows}
          readOnly={readOnly}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder=" "
          className={[
            'peer w-full bg-transparent dark:text-slate-100 outline-none resize-none text-[13.5px]',
            'pt-6 pb-2.5',
            icon ? 'pl-9 pr-3' : 'pl-3 pr-3',
          ].join(' ')}
        />
        {/*
          `right-3` + `truncate` keep the label on ONE line. Unbounded, a long
          label ("Notes, immunization history, or other details…") wrapped to two
          lines once uppercased, and the textarea's `pt-6` only clears one — so
          the second line printed on top of whatever the patient had typed.
        */}
        <label className={[
          'absolute pointer-events-none transition-all duration-200 truncate right-3',
          icon ? 'left-9' : 'left-3',
          hasValue
            ? 'top-2 text-[10px] font-semibold uppercase tracking-wider text-brand-500 dark:text-brand-400'
            : 'top-4 text-[13px] text-slate-400 dark:text-slate-500',
          'peer-focus:top-2 peer-focus:text-[10px] peer-focus:font-semibold peer-focus:uppercase peer-focus:tracking-wider peer-focus:text-brand-500 dark:peer-focus:text-brand-400',
        ].join(' ')}>
          {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
      </div>
      {error && (
        <div className="flex items-center gap-1 mt-1 ml-0.5">
          <AlertCircle size={11} className="text-red-500 shrink-0" />
          <p className="text-[11px] text-red-500">{error}</p>
        </div>
      )}
    </div>
  );
};

/* ─── MultiChipSelect ──────────────────────────────────────────────────────── */
export const MultiChipSelect: React.FC<{
  options: string[];
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  chipColor?: string;
  chipClass?: string;
  error?: string;
  endAction?: React.ReactNode;
  label?: string;
}> = ({ options, value, onChange, placeholder, chipColor, chipClass: chipClassProp, error, endAction, label }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const { triggerRef, position } = useDropdownPosition(open);
  const listRef = useRef<HTMLDivElement>(null);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const available = options.filter(o => !value.includes(o));
  const filtered = search.trim() ? available.filter(o => o.toLowerCase().includes(search.toLowerCase())) : available;

  const defaultChip = 'bg-brand-50 text-brand-700 border-brand-200/60 dark:bg-brand-500/10 dark:text-brand-400 dark:border-brand-500/25';
  const chipClass = chipColor || chipClassProp || defaultChip;

  useEffect(() => { setHighlightIdx(-1); }, [filtered.length, search]);

  useEffect(() => {
    if (highlightIdx >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-option]');
      items[highlightIdx]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx]);

  const selectOption = useCallback((opt: string) => {
    onChange([...value, opt]);
    setSearch('');
    setOpen(false);
    setHighlightIdx(-1);
  }, [value, onChange]);

  return (
    <div className="relative" ref={triggerRef}>
      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => { setOpen(false); setSearch(''); setHighlightIdx(-1); }} />
          {position.ready && (
            <div
              ref={listRef}
              style={{
                position: 'fixed',
                zIndex: 9999,
                maxHeight: position.maxHeight,
                width: position.width,
                left: position.left,
                ...(position.direction === 'above' ? { bottom: position.bottom } : { top: position.top }),
              }}
              className="overflow-y-auto bg-white dark:bg-surface-dark-400 border border-slate-200/80 dark:border-white/[0.10] rounded-xl shadow-lg dark:shadow-dark-lg overscroll-contain animate-fade-in py-1"
            >
              {search.trim() && (
                <div className="px-3 pt-2 pb-1.5 flex items-center gap-1.5 border-b border-slate-100 dark:border-white/[0.06]">
                  <Search size={12} className="text-slate-400 flex-shrink-0" />
                  <span className="text-[11px] text-slate-400 truncate">
                    "<span className="font-semibold text-slate-600 dark:text-slate-300">{search}</span>"
                  </span>
                </div>
              )}
              {filtered.length > 0 ? (
                filtered.map((opt, idx) => (
                  <button key={opt} type="button" data-option
                    onMouseDown={(e) => { e.preventDefault(); selectOption(opt); }}
                    onMouseEnter={() => setHighlightIdx(idx)}
                    className={[
                      'w-full text-left px-3 py-2 text-[13px] transition-colors',
                      idx === highlightIdx
                        ? 'bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400 font-semibold'
                        : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/[0.04]',
                    ].join(' ')}>
                    {opt}
                  </button>
                ))
              ) : (
                <div className="px-3 py-4 text-[12px] text-slate-400 text-center">
                  {available.length === 0 ? 'All options selected' : `No match for "${search}"`}
                </div>
              )}
            </div>
          )}
        </>,
        document.body
      )}

      {label && (
        <label className="block mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-brand-500 dark:text-brand-400">{label}</label>
      )}

      <div
        className={[
          'flex flex-wrap gap-1.5 p-2 border rounded-lg bg-white dark:bg-surface-dark-300 min-h-[42px] items-center cursor-text transition-all duration-200',
          'focus-within:border-brand-400 dark:focus-within:border-brand-400/60 focus-within:ring-2 focus-within:ring-brand-500/[0.08]',
          error ? 'border-red-300 dark:border-red-500/50' : 'border-slate-200 dark:border-white/[0.10] hover:border-slate-300 dark:hover:border-white/[0.16]',
        ].join(' ')}
        onClick={() => setOpen(true)}
      >
        {value.map(v => (
          <span key={v} className={`inline-flex items-center gap-1 px-2 py-[3px] text-[11.5px] font-medium rounded-md border transition-all ${chipClass}`}>
            {v}
            <button type="button" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onChange(value.filter(x => x !== v)); }}
              className="hover:text-red-500 transition-colors flex-shrink-0 opacity-60 hover:opacity-100 ml-0.5">
              <X size={11} />
            </button>
          </span>
        ))}
        <input
          placeholder={value.length === 0 ? (placeholder || 'Search or select…') : ''}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIdx(i => Math.min(i + 1, filtered.length - 1)); if (!open) setOpen(true); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightIdx(i => Math.max(i - 1, 0)); }
            else if (e.key === 'Enter') {
              e.preventDefault();
              if (highlightIdx >= 0 && highlightIdx < filtered.length) { selectOption(filtered[highlightIdx]); }
              else if (search.trim()) { const exact = available.find(o => o.toLowerCase() === search.trim().toLowerCase()); selectOption(exact || search.trim()); }
            }
            else if (e.key === 'Escape') { setOpen(false); setSearch(''); setHighlightIdx(-1); }
            else if (e.key === 'Backspace' && !search && value.length > 0) { onChange(value.slice(0, -1)); }
          }}
          className="flex-1 min-w-[100px] px-1 py-0.5 text-[13px] outline-none bg-transparent dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500"
        />
        {endAction}
      </div>

      {error && (
        <div className="flex items-center gap-1 mt-1 ml-0.5">
          <AlertCircle size={11} className="text-red-500 shrink-0" />
          <p className="text-[11px] text-red-500">{error}</p>
        </div>
      )}
    </div>
  );
};

/* ─── Single Select (Searchable Dropdown) ──────────────────────────────────── */
export const SingleSelect: React.FC<{
  options: string[];
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  error?: string;
}> = ({ options, value, onChange, placeholder, error }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const { triggerRef, position } = useDropdownPosition(open);
  const listRef = useRef<HTMLDivElement>(null);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const filtered = search.trim() ? options.filter(o => o.toLowerCase().includes(search.toLowerCase())) : options;
  const handleClose = () => { setOpen(false); setSearch(''); setHighlightIdx(-1); };

  useEffect(() => { setHighlightIdx(-1); }, [filtered.length, search]);

  useEffect(() => {
    if (highlightIdx >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-option]');
      items[highlightIdx]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx]);

  return (
    <div className="relative w-full" ref={triggerRef}>
      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={handleClose} />
          {position.ready && (
            <div
              ref={listRef}
              style={{
                position: 'fixed',
                zIndex: 9999,
                maxHeight: position.maxHeight,
                width: position.width,
                left: position.left,
                ...(position.direction === 'above' ? { bottom: position.bottom } : { top: position.top }),
              }}
              className="overflow-y-auto bg-white dark:bg-surface-dark-400 border border-slate-200/80 dark:border-white/[0.10] rounded-xl shadow-lg dark:shadow-dark-lg overscroll-contain animate-fade-in py-1"
            >
              {search.trim() && (
                <div className="px-3 pt-2 pb-1.5 flex items-center gap-1.5 border-b border-slate-100 dark:border-white/[0.06]">
                  <Search size={12} className="text-slate-400 flex-shrink-0" />
                  <span className="text-[11px] text-slate-400 truncate">
                    "<span className="font-semibold text-slate-600 dark:text-slate-300">{search}</span>"
                  </span>
                </div>
              )}
              {filtered.length > 0 ? (
                filtered.map((opt, idx) => (
                  <button key={opt} type="button" data-option
                    onMouseDown={(e) => { e.preventDefault(); onChange(opt); handleClose(); }}
                    onMouseEnter={() => setHighlightIdx(idx)}
                    className={[
                      'w-full text-left px-3 py-2 text-[13px] transition-colors',
                      idx === highlightIdx
                        ? 'bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400 font-semibold'
                        : opt === value
                          ? 'bg-brand-50/60 text-brand-600 dark:bg-brand-500/[0.06] dark:text-brand-400 font-medium'
                          : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/[0.04]',
                    ].join(' ')}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{opt}</span>
                      {opt === value && <Check size={12} className="text-brand-500 flex-shrink-0" />}
                    </div>
                  </button>
                ))
              ) : (
                <div className="px-3 py-4 text-[12px] text-slate-400 text-center">No match for "{search}"</div>
              )}
            </div>
          )}
        </>,
        document.body
      )}

      {/* Trigger */}
      <div
        className={[
          'flex items-center rounded-lg border bg-white dark:bg-surface-dark-300 h-[40px] cursor-text transition-all duration-200',
          'focus-within:border-brand-400 dark:focus-within:border-brand-400/60 focus-within:ring-2 focus-within:ring-brand-500/[0.08]',
          error ? 'border-red-300 dark:border-red-500/50' : 'border-slate-200 dark:border-white/[0.10] hover:border-slate-300 dark:hover:border-white/[0.16]',
        ].join(' ')}
        onClick={() => setOpen(true)}
      >
        <input
          placeholder={!value ? (placeholder || 'Select…') : ''}
          value={open ? search : value}
          readOnly={!open}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIdx(i => Math.min(i + 1, filtered.length - 1)); if (!open) setOpen(true); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightIdx(i => Math.max(i - 1, 0)); }
            else if (e.key === 'Enter') {
              e.preventDefault();
              if (highlightIdx >= 0 && highlightIdx < filtered.length) { onChange(filtered[highlightIdx]); handleClose(); }
              else if (search.trim()) { onChange(options.find(o => o.toLowerCase() === search.trim().toLowerCase()) || search.trim()); handleClose(); }
            }
            else if (e.key === 'Escape') handleClose();
          }}
          className="flex-1 w-full px-3 text-[13px] outline-none bg-transparent text-slate-700 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500"
        />
        <ChevronDown size={13} className={`mr-2.5 text-slate-400 transition-transform duration-200 shrink-0 ${open ? 'rotate-180' : ''}`} />
      </div>

      {error && (
        <div className="flex items-center gap-1 mt-1 ml-0.5">
          <AlertCircle size={11} className="text-red-500 shrink-0" />
          <p className="text-[11px] text-red-500">{error}</p>
        </div>
      )}
    </div>
  );
};

/* ─── ConditionCategoryChip — premium compact chip for medical category selection ── */
/**
 * Replaces the inline category selection buttons in MedicalHistory.tsx.
 * Use inside a flex-wrap container. Includes icon, compact label, and check state.
 */
export const ConditionCategoryChip: React.FC<{
  label: string;
  icon?: React.ReactNode;
  selected: boolean;
  onClick: () => void;
}> = ({ label, icon, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      'inline-flex items-center gap-1.5 rounded-full px-3 py-[5px] text-[12px] font-semibold border transition-all duration-200 select-none',
      selected
        ? 'bg-brand-600 text-white border-brand-600 shadow-sm shadow-brand-600/20'
        : 'bg-white dark:bg-surface-dark-300 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-white/[0.10] hover:border-brand-400 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-brand-50/30',
      'active:scale-[0.97] cursor-pointer',
    ].join(' ')}
  >
    {selected
      ? <Check size={11} strokeWidth={3} className="shrink-0 opacity-90" />
      : icon && <span className="shrink-0 opacity-60 [&>svg]:h-[12px] [&>svg]:w-[12px]">{icon}</span>
    }
    {/* Strip trailing asterisk + "Conditions" jargon to keep chip compact */}
    {label.replace(' *', '').replace('Diagnoses & Treatments', 'Diagnoses').replace('Surgical History', 'Surgeries')}
  </button>
);

/* ─── FamilyHistoryRow — premium compact row for Add Family Member UI ────────── */
/**
 * A clean, compact row for entering a family member's relation and condition.
 * Replaces the raw SingleSelect pair in MedicalHistory.tsx.
 */
export const FamilyHistoryRow: React.FC<{
  index: number;
  relation: string;
  disease: string;
  onRelationChange: (v: string) => void;
  onDiseaseChange: (v: string) => void;
  onRemove: () => void;
  relationOptions: string[];
  diseaseOptions: string[];
}> = ({ index, relation, disease, onRelationChange, onDiseaseChange, onRemove, relationOptions, diseaseOptions }) => (
  <div className="group flex items-start gap-2 p-2.5 rounded-xl border border-slate-200/70 dark:border-white/[0.07] bg-slate-50/50 dark:bg-white/[0.02] hover:border-slate-300 dark:hover:border-white/[0.10] transition-all duration-150 animate-fade-in">
    {/* Row number badge */}
    <div className="flex-shrink-0 w-5 h-5 mt-2 rounded-full bg-slate-200 dark:bg-white/[0.08] flex items-center justify-center">
      <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500">{index + 1}</span>
    </div>

    {/*
      The two selects STACK on a phone. Side by side, the relation column held a
      fixed 140px it could not give up, so on a ~300px card the condition select
      was squeezed to roughly 50px — and since the menu is rendered at the
      trigger's width, its options collapsed to "C…", "H…" and were unreadable.
    */}
    <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row">
      {/* Relation */}
      <div className="w-full sm:w-[140px] sm:flex-shrink-0">
        <SingleSelect
          options={relationOptions}
          value={relation}
          onChange={onRelationChange}
          placeholder="Relation"
        />
      </div>

      {/* Disease/condition — fills the remaining space from `sm` up */}
      <div className="w-full min-w-0 sm:flex-1">
        <SingleSelect
          options={diseaseOptions}
          value={disease}
          onChange={onDiseaseChange}
          placeholder="Condition / Disease"
        />
      </div>
    </div>

    {/* Remove button */}
    <button
      type="button"
      onClick={onRemove}
      className="flex-shrink-0 w-7 h-7 mt-1.5 rounded-lg flex items-center justify-center text-slate-300 dark:text-slate-600 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all can-hover:opacity-0 can-hover:group-hover:opacity-100"
      title="Remove"
      aria-label="Remove family member"
    >
      <Trash2 size={13} />
    </button>
  </div>
);

/* ─── AddFamilyMemberButton — premium dashed "Add" button ───────────────────── */
export const AddFamilyMemberButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      'inline-flex items-center gap-1.5 mt-2 px-3.5 py-[7px] rounded-lg text-[12px] font-semibold',
      'border border-dashed border-brand-300 dark:border-brand-500/30',
      'text-brand-600 dark:text-brand-400',
      'hover:bg-brand-50 dark:hover:bg-brand-500/[0.06] hover:border-brand-400 dark:hover:border-brand-500/50',
      'transition-all duration-200 active:scale-[0.97]',
    ].join(' ')}
  >
    <Plus size={13} strokeWidth={2.5} className="shrink-0" />
    Add Family Member
  </button>
);

/* ─── Delete Confirmation Modal ────────────────────────────────────── */
export const DeleteConfirmModal: React.FC<{
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
}> = ({ open, onCancel, onConfirm, title = 'Delete Document', message = 'Are you sure you want to delete this document? This action cannot be undone.' }) => {
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in">
      <div className="bg-white dark:bg-surface-dark-300 rounded-2xl shadow-xl p-5 max-w-sm w-full mx-4 border border-slate-200/40 dark:border-white/[0.10] animate-scale-in">
        <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-500/10 flex items-center justify-center mb-3.5">
          <AlertCircle size={18} className="text-red-500" />
        </div>
        <h3 className="text-[14px] font-bold text-slate-900 dark:text-slate-100 mb-1">{title}</h3>
        <p className="text-[12.5px] text-slate-500 dark:text-slate-400 mb-4 leading-relaxed">{message}</p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-3.5 py-2 text-[12.5px] font-medium rounded-lg border border-slate-200 dark:border-white/[0.10] hover:bg-slate-50 dark:hover:bg-white/[0.05] transition-all text-slate-600 dark:text-slate-300 active:scale-[0.98]">Cancel</button>
          <button onClick={onConfirm} className="px-3.5 py-2 text-[12.5px] font-semibold rounded-lg bg-red-500 text-white hover:bg-red-600 transition-all shadow-sm shadow-red-500/20 active:scale-[0.98]">Delete</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ───  Preview Modal ────────────────────────────────────────────────── */
export const PreviewModal: React.FC<{ items: { url: string; fileName: string }[]; currentIndex: number; onClose: () => void; onIndexChange: (i: number) => void; }> = ({ items, currentIndex, onClose, onIndexChange }) => {

  if (items.length === 0) return null;
  const current = items[currentIndex];
  const isImage = current?.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
  const onPrev = () => onIndexChange(Math.max(0, currentIndex - 1));
  const onNext = () => onIndexChange(Math.min(items.length - 1, currentIndex + 1));
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === items.length - 1;

  return createPortal(
    <div
      className="fixed inset-0 z-[9991] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-surface-dark-50 rounded-2xl shadow-xl w-[90%] max-w-[1200px] max-h-[85vh] overflow-hidden flex flex-col relative border border-slate-200/40 dark:border-surface-dark-200 animate-scale-in"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 pt-3 pb-2.5 border-b border-slate-100 dark:border-white/[0.06] shrink-0">
          {items.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); onPrev(); }}
              disabled={isFirst}
              className={`p-1.5 rounded-lg border transition-all shrink-0 ${isFirst
                ? 'opacity-30 cursor-not-allowed border-slate-200 dark:border-white/[0.08] text-slate-400'
                : 'border-slate-200 dark:border-white/[0.10] text-slate-500 hover:bg-slate-50 dark:hover:bg-white/[0.06] hover:text-brand-600'
                }`}
            >
              <ChevronLeft size={15} />
            </button>
          )}
          <h3 className="flex-1 text-[12px] font-semibold text-center text-slate-700 dark:text-slate-200 truncate min-w-0">
            {current?.fileName}
          </h3>
          <div className="flex items-center gap-1.5 shrink-0">
            {items.length > 1 && (
              <>
                <span className="text-[11px] text-slate-400 tabular-nums">{currentIndex + 1}&nbsp;/&nbsp;{items.length}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); onNext(); }}
                  disabled={isLast}
                  className={`p-1.5 rounded-lg border transition-all ${isLast
                    ? 'opacity-30 cursor-not-allowed border-slate-200 dark:border-white/[0.08] text-slate-400'
                    : 'border-slate-200 dark:border-white/[0.10] text-slate-500 hover:bg-slate-50 dark:hover:bg-white/[0.06] hover:text-brand-600'
                    }`}
                >
                  <ChevronRight size={15} />
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 border border-transparent hover:border-red-200/60 transition-all ml-1"
            >
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isImage ? (
            <img src={current.url} alt={current.fileName} className="w-full" />
          ) : (
            <iframe
              src={current?.url}
              title={current?.fileName}
              className="w-full border-none"
              style={{ height: 'calc(85vh - 53px)' }}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ─── Date Picker (Shadcn: Popover + Calendar) ──────────────────────────────── */

function isoToDate(iso: string | null): Date | undefined {
  if (!iso) return undefined;
  try { const d = new Date(iso); return isNaN(d.getTime()) ? undefined : d; } catch { return undefined; }
}

export const DatePickerField: React.FC<{
  label: string;
  value: string | null;
  onChange: (iso: string | null) => void;
  required?: boolean;
  error?: string;
  disableFuture?: boolean;
  disablePast?: boolean;
}> = ({ label, value, onChange, required, error, disableFuture = false, disablePast = false }) => {
  const [open, setOpen] = useState(false);
  const selectedDate = isoToDate(value);

  const displayValue = selectedDate ? dateFnsFormat(selectedDate, 'dd/MM/yyyy') : '';
  const hasValue = !!displayValue;

  const disabledMatcher = React.useMemo(() => {
    const matchers: any[] = [];
    if (disableFuture) matchers.push({ after: new Date() });
    if (disablePast) matchers.push({ before: new Date(new Date().setHours(0, 0, 0, 0)) });
    return matchers.length > 0 ? matchers : undefined;
  }, [disableFuture, disablePast]);

  const borderClass = error
    ? 'border-red-300 dark:border-red-500/50'
    : open
      ? 'border-brand-400 dark:border-brand-400/60 ring-2 ring-brand-500/[0.08]'
      : 'border-slate-200 dark:border-white/[0.12] hover:border-slate-300 dark:hover:border-white/[0.20]';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="flex flex-col gap-1">
        {/* Label */}
        <label className={['text-[11.5px] font-medium leading-none select-none', error ? 'text-red-500' : 'text-slate-500 dark:text-slate-400'].join(' ')}>
          {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>

        {/* Trigger — matches FloatingLabelInput h-[42px] style */}
        <PopoverTrigger asChild>
          <button
            type="button"
            className={['relative flex items-center w-full rounded-lg border bg-white dark:bg-surface-dark-300 transition-all duration-150 h-[42px] cursor-pointer text-left', borderClass].join(' ')}
          >
            <CalendarIcon size={14} className="absolute left-3 text-slate-400 dark:text-slate-500 pointer-events-none" />
            <span className={['flex-1 pl-9 pr-8 text-[13.5px] font-normal', hasValue ? 'text-slate-800 dark:text-slate-100' : 'text-slate-400 dark:text-slate-500'].join(' ')}>
              {hasValue ? displayValue : 'Select date…'}
            </span>
            <ChevronDown size={14} className={['absolute right-3 text-slate-400 transition-transform duration-200', open ? 'rotate-180' : ''].join(' ')} />
          </button>
        </PopoverTrigger>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-1 mt-0.5">
            <AlertCircle size={11} className="text-red-500 shrink-0" />
            <p className="text-[11px] text-red-500">{error}</p>
          </div>
        )}
      </div>

      {/* Shadcn Calendar in Popover — captionLayout="dropdown-buttons" allows direct month/year pick */}
      <PopoverContent className="p-0 w-auto" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(date) => { onChange(date ? date.toISOString() : null); setOpen(false); }}
          disabled={disabledMatcher}
          initialFocus
          fromDate={disablePast ? new Date(new Date().setHours(0, 0, 0, 0)) : undefined}
          toDate={disableFuture ? new Date() : undefined}
          fromYear={disablePast ? new Date().getFullYear() : new Date().getFullYear() - 100}
          toYear={disableFuture ? new Date().getFullYear() : new Date().getFullYear() + 10}
        />
        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 dark:border-white/[0.06]">
          <button type="button" onClick={() => { onChange(null); setOpen(false); }} className="text-[11.5px] font-medium text-slate-400 hover:text-red-500 transition-colors">Clear</button>
          <button type="button" onClick={() => { onChange(new Date().toISOString()); setOpen(false); }} className="text-[11.5px] font-semibold text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors">Today</button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

